import { validateLibrary } from "../domain/backup";
import { nowIso } from "../domain/format";
import { SCHEMA_VERSION, type Library } from "../domain/types";
import type { LibraryRepository, LoadResult } from "./repository";

export const STORE_KEY = "memolink-app-v2";
export const SAFETY_KEY = "memolink-app-v2:safety-copy";
/** key the Queue 1 prototype used. Read only; this app never writes or deletes it. */
export const PROTOTYPE_KEY = "memolink-proto-v1";

interface Envelope { format: "memolink-store"; version: number; savedAt: string; library: Library }

export class LocalStorageRepository implements LibraryRepository {
  readonly label = "localStorage ของเบราว์เซอร์นี้";
  constructor(private key = STORE_KEY, private storage: Storage = globalThis.localStorage) {}

  async load(): Promise<LoadResult> {
    let raw: string | null;
    try { raw = this.storage.getItem(this.key); }
    catch { return { status: "error", error: "เบราว์เซอร์นี้ไม่อนุญาตให้อ่านข้อมูลที่บันทึกไว้" }; }
    if (raw == null) return { status: "empty" };
    try {
      const env = JSON.parse(raw) as Envelope;
      if (env?.format !== "memolink-store") throw new Error("format");
      const v = validateLibrary(env.library, env.library?.settings);
      if (!v.ok) throw new Error(v.errors.join("; "));
      return { status: "ok", library: v.library };
    } catch (e) {
      // keep the unreadable copy aside so a later save can never silently erase it
      try { this.storage.setItem(`${this.key}:unreadable-${Date.now()}`, raw); } catch { /* storage full */ }
      return { status: "error", error: "ข้อมูลที่บันทึกไว้อ่านไม่ได้ เก็บสำเนาไว้แยกแล้ว" };
    }
  }

  async save(lib: Library): Promise<void> {
    const env: Envelope = { format: "memolink-store", version: SCHEMA_VERSION, savedAt: nowIso(), library: lib };
    this.storage.setItem(this.key, JSON.stringify(env));
  }

  async clear(): Promise<void> { this.storage.removeItem(this.key); }

  async saveSafetyCopy(lib: Library, reason: string) {
    this.storage.setItem(SAFETY_KEY, JSON.stringify({ savedAt: nowIso(), reason, library: lib }));
  }
  async loadSafetyCopy() {
    try {
      const raw = this.storage.getItem(SAFETY_KEY); if (!raw) return null;
      const o = JSON.parse(raw);
      const v = validateLibrary(o.library, o.library?.settings);
      return v.ok ? { library: v.library, savedAt: String(o.savedAt), reason: String(o.reason) } : null;
    } catch { return null; }
  }
  async clearSafetyCopy() { try { this.storage.removeItem(SAFETY_KEY); } catch { /* ignore */ } }
}

/** Prototype data, only when the prototype ran on this same origin (usually it did not: it ran as a claude.ai artifact). */
export function readPrototypeData(storage: Storage = globalThis.localStorage): string | null {
  try { return storage.getItem(PROTOTYPE_KEY); } catch { return null; }
}
